import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right' | 'auto';
export type TooltipVariant = 'dark' | 'light' | 'slate';

// Resolved colors per variant. `dark` is the brand lavender (default used across
// the app); `light` is the white card style; `slate` is the solid high-contrast
// dark tooltip (deep navy bg, white text) for icon-only targets like the
// collapsed sidebar rail.
const VARIANT_STYLES: Record<TooltipVariant, { fill: string; stroke: string; textClass: string }> = {
  dark: { fill: '#e8eaf2', stroke: '#d5d9e8', textClass: 'text-primary-900' }, // primary-100 / primary-200
  light: { fill: '#ffffff', stroke: '#e5e7eb', textClass: 'text-neutral-800' },
  slate: { fill: '#131827', stroke: '#131827', textClass: 'text-white' }, // primary-900
};

export interface TooltipProps {
  children: React.ReactElement;
  content: React.ReactNode;
  position?: TooltipPosition;
  variant?: TooltipVariant;
  delay?: number;
  maxWidth?: number | string;
  disabled?: boolean;
  className?: string;
}

type ResolvedPosition = Exclude<TooltipPosition, 'auto'>;

const GAP = 8;
const VIEWPORT_PAD = 8;

function resolvePosition(
  trigger: DOMRect,
  tip: DOMRect,
  preferred: TooltipPosition,
): ResolvedPosition {
  if (preferred !== 'auto') return preferred;
  const space = {
    top: trigger.top,
    bottom: window.innerHeight - trigger.bottom,
    left: trigger.left,
    right: window.innerWidth - trigger.right,
  };
  const need = { top: tip.height + GAP, bottom: tip.height + GAP, left: tip.width + GAP, right: tip.width + GAP };
  const order: ResolvedPosition[] = ['top', 'bottom', 'right', 'left'];
  for (const side of order) {
    if (space[side] >= need[side]) return side;
  }
  // Fallback to whichever side has the most room.
  return (Object.keys(space) as ResolvedPosition[]).reduce((a, b) => (space[a] >= space[b] ? a : b));
}

function computeCoords(trigger: DOMRect, tip: DOMRect, side: ResolvedPosition) {
  let top = 0;
  let left = 0;
  switch (side) {
    case 'top':
      top = trigger.top - tip.height - GAP;
      left = trigger.left + trigger.width / 2 - tip.width / 2;
      break;
    case 'bottom':
      top = trigger.bottom + GAP;
      left = trigger.left + trigger.width / 2 - tip.width / 2;
      break;
    case 'left':
      top = trigger.top + trigger.height / 2 - tip.height / 2;
      left = trigger.left - tip.width - GAP;
      break;
    case 'right':
      top = trigger.top + trigger.height / 2 - tip.height / 2;
      left = trigger.right + GAP;
      break;
  }
  // Clamp inside viewport.
  left = Math.max(VIEWPORT_PAD, Math.min(left, window.innerWidth - tip.width - VIEWPORT_PAD));
  top = Math.max(VIEWPORT_PAD, Math.min(top, window.innerHeight - tip.height - VIEWPORT_PAD));
  return { top, left };
}

export default function Tooltip({
  children,
  content,
  position = 'top',
  variant = 'dark',
  delay = 200,
  maxWidth = 260,
  disabled = false,
  className = '',
}: TooltipProps) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const [side, setSide] = useState<ResolvedPosition>('top');
  const triggerRef = useRef<HTMLElement | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);
  const timerRef = useRef<number | null>(null);
  const id = useId();

  const show = useCallback(() => {
    if (disabled || !content) return;
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setOpen(true), delay);
  }, [disabled, content, delay]);

  const hide = useCallback(() => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    setOpen(false);
  }, []);

  useEffect(() => () => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
  }, []);

  // Position once the tooltip mounts and on resize/scroll while open.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !tipRef.current) return;
    const place = () => {
      const t = triggerRef.current!.getBoundingClientRect();
      const tip = tipRef.current!.getBoundingClientRect();
      const resolved = resolvePosition(t, tip, position);
      setSide(resolved);
      setCoords(computeCoords(t, tip, resolved));
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, position, content]);

  // Dismiss on Escape — common a11y expectation.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') hide();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, hide]);

  const child = React.Children.only(children);
  const triggerProps = {
    ref: (node: HTMLElement | null) => {
      triggerRef.current = node;
      const orig = (child as any).ref;
      if (typeof orig === 'function') orig(node);
      else if (orig && typeof orig === 'object') (orig as React.MutableRefObject<HTMLElement | null>).current = node;
    },
    onMouseEnter: (e: React.MouseEvent) => { child.props.onMouseEnter?.(e); show(); },
    onMouseLeave: (e: React.MouseEvent) => { child.props.onMouseLeave?.(e); hide(); },
    onFocus: (e: React.FocusEvent) => { child.props.onFocus?.(e); show(); },
    onBlur: (e: React.FocusEvent) => { child.props.onBlur?.(e); hide(); },
    'aria-describedby': open ? id : child.props['aria-describedby'],
  };

  const { fill, stroke, textClass } = VARIANT_STYLES[variant];

  const ARROW_W = 12;
  const ARROW_H = 7;
  const arrowStyle: Record<ResolvedPosition, React.CSSProperties> = {
    top: { left: '50%', transform: 'translateX(-50%)', bottom: -ARROW_H + 1, width: ARROW_W, height: ARROW_H },
    bottom: { left: '50%', transform: 'translateX(-50%) rotate(180deg)', top: -ARROW_H + 1, width: ARROW_W, height: ARROW_H },
    left: { top: '50%', transform: 'translateY(-50%) rotate(-90deg)', right: -((ARROW_W + ARROW_H) / 2) + (ARROW_H / 2) + 1, width: ARROW_W, height: ARROW_H },
    right: { top: '50%', transform: 'translateY(-50%) rotate(90deg)', left: -((ARROW_W + ARROW_H) / 2) + (ARROW_H / 2) + 1, width: ARROW_W, height: ARROW_H },
  };

  return (
    <>
      {React.cloneElement(child, triggerProps)}
      {open && content && typeof document !== 'undefined' && createPortal(
        <div
          ref={tipRef}
          id={id}
          role="tooltip"
          className={`fixed z-[9999] pointer-events-none animate-fade-in ${className}`}
          style={{
            top: coords?.top ?? -9999,
            left: coords?.left ?? -9999,
            maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth,
            visibility: coords ? 'visible' : 'hidden',
          }}
        >
          <div
            className={`relative px-2.5 py-1.5 text-xs leading-relaxed rounded-md shadow-lg ${textClass}`}
            style={{ backgroundColor: fill, border: `1px solid ${stroke}` }}
          >
            {content}
            <svg
              aria-hidden
              viewBox={`0 0 ${ARROW_W} ${ARROW_H}`}
              className="absolute block"
              style={arrowStyle[side]}
            >
              {/* Fill polygon */}
              <polygon points={`0,0 ${ARROW_W},0 ${ARROW_W / 2},${ARROW_H}`} fill={fill} />
              {/* Stroke the two slanted edges only — leaves the top open so it joins seamlessly with the tooltip body */}
              <polyline
                points={`0,0 ${ARROW_W / 2},${ARROW_H} ${ARROW_W},0`}
                fill="none"
                stroke={stroke}
                strokeWidth="1"
                strokeLinejoin="miter"
              />
            </svg>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
